import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  useCommandState,
} from "~/components/shared/Command";
import Icon from "~/components/shared/Icon";

interface CommandPaletteAction {
  id: string;
  title: string;
  section: string;
  url: string;
  keywords?: string;
}

interface CommandPaletteProps {
  actionsJson: string;
  commandTargetId: string;
  documentationUrl: string;
  queryFormId: string;
  queryInputId: string;
}

interface CommandEventLike extends Event {
  command?: string;
}

function filterActions(_value: string, search: string, keywords: string[] = []) {
  const query = search.trim().toLowerCase();

  if (!query) {
    return 1;
  }

  return keywords.join(" ").toLowerCase().includes(query) ? 1 : 0;
}

function EmptyState() {
  const search = useCommandState((state) => state.search.trim());

  if (!search) {
    return (
      <CommandEmpty className="px-4 py-10">
        <div className="dark:bg-charcoal-800/80 mx-auto flex max-w-sm flex-col items-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-7 text-center dark:border-white/10">
          <div className="text-charcoal-500 dark:bg-charcoal-700 mb-3 flex size-10 items-center justify-center rounded-2xl bg-white ring-1 ring-gray-200 dark:text-gray-200 dark:ring-white/10">
            <Icon icon="heroicons/16/solid/magnifying-glass" className="size-4" />
          </div>
          <p className="text-charcoal-700 text-sm font-medium dark:text-white">
            No commands available
          </p>
          <p className="text-charcoal-400 mt-1 text-xs dark:text-slate-400">
            Try again after the admin navigation finishes loading.
          </p>
        </div>
      </CommandEmpty>
    );
  }

  return (
    <CommandEmpty className="px-4 py-10">
      <div className="dark:bg-charcoal-800/80 mx-auto flex max-w-sm flex-col items-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-7 text-center dark:border-white/10">
        <div className="text-charcoal-500 dark:bg-charcoal-700 mb-3 flex size-10 items-center justify-center rounded-2xl bg-white ring-1 ring-gray-200 dark:text-gray-200 dark:ring-white/10">
          <Icon icon="heroicons/16/solid/arrow-turn-down-left" className="size-4" />
        </div>
        <p className="text-charcoal-700 text-sm font-medium dark:text-white">
          No direct matches for “{search}”
        </p>
        <p className="text-charcoal-400 mt-1 text-xs dark:text-slate-400">
          Press Enter to run that query through the admin navigation guide.
        </p>
      </div>
    </CommandEmpty>
  );
}

function SearchSummary() {
  const search = useCommandState((state) => state.search.trim());
  const filteredCount = useCommandState((state) => state.filtered.count);

  if (!search) {
    return (
      <p className="text-charcoal-500 text-sm dark:text-slate-400">
        Browse shortcuts, pages, and deep-link queries
      </p>
    );
  }

  if (filteredCount > 0) {
    return (
      <p className="text-charcoal-500 text-sm dark:text-slate-400">
        {filteredCount} match{filteredCount === 1 ? "" : "es"} for “{search}”
      </p>
    );
  }

  return (
    <p className="text-charcoal-500 text-sm dark:text-slate-400">
      No direct matches, but Enter will still search the guide
    </p>
  );
}

function SearchInputHint() {
  const search = useCommandState((state) => state.search.trim());
  const filteredCount = useCommandState((state) => state.filtered.count);

  if (!search) {
    return <span>type to filter</span>;
  }

  if (filteredCount > 0) {
    return <span>enter to open</span>;
  }

  return <span>enter to search</span>;
}

interface FooterHintProps {
  documentationUrl: string;
}

function FooterHint({ documentationUrl }: FooterHintProps) {
  return (
    <div className="text-charcoal-500 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200/80 bg-transparent px-4 py-3 text-[11px] dark:border-white/10 dark:text-slate-400">
      <div className="flex flex-wrap items-center gap-2">
        <kbd className="text-charcoal-600 dark:bg-charcoal-800 inline-flex h-5 min-w-[2rem] items-center justify-center rounded-md border border-gray-300 bg-white px-1.5 font-semibold uppercase tracking-wide dark:border-white/10 dark:text-gray-200">
          ↑↓
        </kbd>
        <span>move</span>
        <kbd className="text-charcoal-600 dark:bg-charcoal-800 inline-flex h-5 min-w-[2rem] items-center justify-center rounded-md border border-gray-300 bg-white px-1.5 font-semibold uppercase tracking-wide dark:border-white/10 dark:text-gray-200">
          ↵
        </kbd>
        <span>open</span>
        <kbd className="text-charcoal-600 dark:bg-charcoal-800 inline-flex h-5 min-w-[2.4rem] items-center justify-center rounded-md border border-gray-300 bg-white px-1.5 font-semibold uppercase tracking-wide dark:border-white/10 dark:text-gray-200">
          esc
        </kbd>
        <span>close</span>
      </div>

      <a
        href={documentationUrl}
        className="text-charcoal-500 hover:text-charcoal-700 text-[11px] font-semibold uppercase tracking-[0.18em] transition hover:underline dark:text-slate-400 dark:hover:text-white"
      >
        Documentation
      </a>
    </div>
  );
}

interface SearchInputProps {
  inputRef: Ref<HTMLInputElement>;
  search: string;
  setSearch: (value: string) => void;
  submitQuery: (query: string) => void;
}

function SearchInput({ inputRef, search, setSearch, submitQuery }: SearchInputProps) {
  const filteredCount = useCommandState((state) => state.filtered.count);
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter" || filteredCount > 0) {
        return;
      }

      const query = search.trim();

      if (!query) {
        return;
      }

      event.preventDefault();
      submitQuery(query);
    },
    [filteredCount, search, submitQuery],
  );

  return (
    <div className="border-b border-gray-200/80 bg-transparent px-4 pb-4 pt-4 dark:border-white/10">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="text-charcoal-600 dark:bg-charcoal-800 flex size-10 shrink-0 items-center justify-center rounded-2xl bg-gray-100 dark:text-gray-100">
            <Icon icon="heroicons/16/solid/magnifying-glass" className="size-4" />
          </div>

          <div className="min-w-0">
            <p className="text-charcoal-500 text-[11px] font-semibold uppercase tracking-[0.24em] dark:text-slate-400">
              Admin Command Palette
            </p>
            <SearchSummary />
          </div>
        </div>

        <kbd className="text-charcoal-600 dark:bg-charcoal-800 inline-flex h-7 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white px-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] dark:border-white/10 dark:text-gray-200">
          cmd+k
        </kbd>
      </div>

      <div className="relative">
        <Icon
          icon="heroicons/16/solid/magnifying-glass"
          className="text-charcoal-400 pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2"
        />

        <CommandInput
          ref={inputRef}
          value={search}
          onValueChange={setSearch}
          placeholder="Search admin navigation, URLs, or Global IDs..."
          className="text-charcoal-700 placeholder:text-charcoal-400 dark:bg-charcoal-800 h-12 rounded-2xl border border-gray-300 bg-white pl-11 pr-28 text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] transition focus:border-lime-500 focus:ring-2 focus:ring-lime-500/20 dark:border-white/10 dark:text-white dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] dark:placeholder:text-gray-500"
          onKeyDown={handleKeyDown}
        />

        <div className="text-charcoal-400 pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] dark:text-gray-500">
          <span className="hidden sm:inline">
            <SearchInputHint />
          </span>
          <span className="text-charcoal-600 dark:bg-charcoal-700 inline-flex h-7 min-w-[2rem] items-center justify-center rounded-lg border border-gray-300 bg-gray-50 px-2 dark:border-white/10 dark:text-gray-200">
            ↵
          </span>
        </div>
      </div>
    </div>
  );
}

function actionKeywords(action: CommandPaletteAction) {
  return [action.title, action.section, ...(action.keywords?.split(/\s+/) ?? [])];
}

function actionIsExternal(action: CommandPaletteAction) {
  return action.url.startsWith("http://") || action.url.startsWith("https://");
}

interface CommandPaletteItemProps {
  action: CommandPaletteAction;
  close: () => void;
}

function CommandPaletteItem({ action, close }: CommandPaletteItemProps) {
  const selected = useCommandState((state) => state.value === action.id);
  const handleSelect = useCallback(() => {
    close();
    Turbo.visit(action.url);
  }, [action.url, close]);

  const isExternal = actionIsExternal(action);

  return (
    <CommandItem
      key={action.id}
      value={action.id}
      keywords={actionKeywords(action)}
      onSelect={handleSelect}
      className="text-charcoal-700 dark:hover:bg-charcoal-800/80 dark:data-[selected=true]:bg-charcoal-700 gap-3 rounded-2xl bg-transparent px-3 py-2.5 transition-colors duration-150 hover:bg-gray-100/90 data-[selected=true]:bg-purple-100 data-[selected=true]:text-purple-700 dark:text-gray-200 dark:data-[selected=true]:text-white"
    >
      <div className="flex min-w-0 flex-1 items-center">
        <p className="truncate whitespace-nowrap text-sm font-medium leading-tight">
          {action.title}
        </p>
      </div>

      <div className="ml-2 flex shrink-0 items-center">
        <Icon
          icon={
            isExternal
              ? "heroicons/16/solid/arrow-top-right-on-square"
              : "heroicons/16/solid/arrow-right"
          }
          data-slot="command-palette-arrow"
          className={
            selected
              ? "size-4 text-purple-700 dark:text-white/80"
              : "text-charcoal-400 size-4 transition-colors duration-150 dark:text-gray-500"
          }
        />
      </div>
    </CommandItem>
  );
}

export default function CommandPalette({
  actionsJson,
  commandTargetId,
  documentationUrl,
  queryFormId,
  queryInputId,
}: CommandPaletteProps) {
  const actions = useMemo(() => JSON.parse(actionsJson) as CommandPaletteAction[], [actionsJson]);
  const groupedActions = useMemo(() => {
    const sections = new Map<string, CommandPaletteAction[]>();

    for (const action of actions) {
      if (!sections.has(action.section)) {
        sections.set(action.section, []);
      }

      sections.get(action.section)?.push(action);
    }

    return Array.from(sections.entries());
  }, [actions]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);

  const submitQuery = useCallback(
    (query: string) => {
      const form = document.getElementById(queryFormId) as HTMLFormElement | null;
      const input = document.getElementById(queryInputId) as HTMLInputElement | null;

      if (!form || !input) {
        return;
      }

      input.value = query;
      close();
      form.requestSubmit();
    },
    [close, queryFormId, queryInputId],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setOpen(true);
        return;
      }

      close();
    },
    [close],
  );

  useEffect(() => {
    const isMac = typeof navigator !== "undefined" && navigator.userAgent.includes("Mac");

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.key.toLowerCase() !== "k") {
        return;
      }

      const shortcutPressed = isMac
        ? event.metaKey && !event.ctrlKey
        : event.ctrlKey && !event.metaKey;

      if (!shortcutPressed) {
        return;
      }

      event.preventDefault();
      setOpen((currentOpen) => !currentOpen);
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    const target = document.getElementById(commandTargetId);

    if (!target) {
      return;
    }

    const onCommand = (event: Event) => {
      if ((event as CommandEventLike).command !== "--open") {
        return;
      }

      setOpen(true);
    };

    target.addEventListener("command", onCommand);

    return () => {
      target.removeEventListener("command", onCommand);
    };
  }, [commandTargetId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [open]);

  return (
    <CommandPrimitive.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      label="Admin command palette"
      aria-describedby={undefined}
      loop={true}
      vimBindings={false}
      filter={filterActions}
      overlayClassName="fixed inset-0 z-50 bg-charcoal-900/45 backdrop-blur-sm dark:bg-white/15"
      contentClassName="not-inverted fixed left-1/2 top-[8vh] z-50 w-[min(46rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-[24px] bg-white shadow-[0_28px_90px_rgba(15,23,42,0.24)] outline-hidden dark:bg-charcoal-900 dark:shadow-[0_32px_100px_rgba(0,0,0,0.55)]"
    >
      <DialogPrimitive.Title className="sr-only">Admin command palette</DialogPrimitive.Title>
      <Command
        vimBindings={false}
        className="dark:bg-charcoal-900 gap-0 bg-white p-0 dark:text-gray-200"
      >
        <SearchInput
          inputRef={inputRef}
          search={search}
          setSearch={setSearch}
          submitQuery={submitQuery}
        />

        <CommandList className="max-h-[min(60vh,34rem)] scroll-py-3 px-3 py-2">
          <EmptyState />

          {groupedActions.map(([section, sectionActions]) => (
            <CommandGroup
              key={section}
              heading={section}
              className="[&_[cmdk-group-heading]]:text-charcoal-500 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-4 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.24em] dark:[&_[cmdk-group-heading]]:text-slate-400"
            >
              {sectionActions.map((action) => (
                <CommandPaletteItem key={action.id} action={action} close={close} />
              ))}
            </CommandGroup>
          ))}
        </CommandList>

        <FooterHint documentationUrl={documentationUrl} />
      </Command>
    </CommandPrimitive.Dialog>
  );
}
